import {
  CALORIE_DAY_VIEWS,
  DAILY_LIMIT_MAX,
  DAILY_LIMIT_MIN,
  NAME_MAX,
  type CalorieDayView,
  type CalorieMealDto,
} from '@iac/contracts';
import { useId, useState, type FormEvent } from 'react';
import { DeleteButton } from '../../components/DeleteButton';
import { Field, SelectField } from '../../components/Field';
import {
  IconChevronDown,
  IconChevronUp,
  IconPencilSquare,
} from '../../components/icons';
import { PageHeader } from '../../components/PageHeader';
import { Panel } from '../../components/Panel';
import { useDict } from '../../i18n/dictionary';
import { usePageTitle } from '../../lib/page';
import {
  useCalorieSettings,
  useCreateMeal,
  useDeleteMeal,
  useMeals,
  useRenameMeal,
  useReorderMeals,
  useUpdateDailyLimit,
  useUpdateDayView,
  useUpdateTargetWeight,
} from './api';
import { caloriesDict } from './i18n';
import { formatWeight } from './links';

export function CaloriesSettings() {
  const t = useDict(caloriesDict);
  usePageTitle(`${t.settings} — ${t.titleSuffix}`);
  const { data: settings } = useCalorieSettings();

  return (
    <>
      <PageHeader title={t.settings} />
      {/* Mounted once loaded, so the field starts from the saved value. */}
      {settings && <LimitForm dailyLimitKcal={settings.dailyLimitKcal} />}
      {settings && (
        <TargetWeightForm targetWeightKg={settings.targetWeightKg} />
      )}
      {settings && <DayViewForm dayView={settings.dayView} />}
      <MealsEditor />
    </>
  );
}

function LimitForm({ dailyLimitKcal }: { dailyLimitKcal: number }) {
  const t = useDict(caloriesDict);
  const [value, setValue] = useState(String(dailyLimitKcal));
  const save = useUpdateDailyLimit();

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    save.mutate(value);
  }

  return (
    <Panel title={t.dailyLimit}>
      <form onSubmit={submit} className="form-stack">
        <Field
          id="calories-daily-limit"
          label={t.dailyLimitKcal}
          type="number"
          inputMode="numeric"
          min={DAILY_LIMIT_MIN}
          max={DAILY_LIMIT_MAX}
          step={1}
          required
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <div className="form-actions">
          <button
            type="submit"
            className="btn-primary"
            disabled={save.isPending}
          >
            {t.save}
          </button>
        </div>
      </form>
    </Panel>
  );
}

/** Optional: an empty field saves no target. */
function TargetWeightForm({
  targetWeightKg,
}: {
  targetWeightKg: number | null;
}) {
  const t = useDict(caloriesDict);
  const [value, setValue] = useState(
    targetWeightKg === null ? '' : formatWeight(targetWeightKg),
  );
  const save = useUpdateTargetWeight();

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    save.mutate(value.trim() === '' ? null : value);
  }

  return (
    <Panel title={t.targetWeight}>
      <form onSubmit={submit} className="form-stack">
        <div>
          <Field
            id="calories-target-weight"
            label={t.targetWeightKg}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            placeholder="78"
            aria-describedby="calories-target-weight-hint"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          <p
            id="calories-target-weight-hint"
            className="text-hint target-weight-hint"
          >
            {t.targetWeightHint}
          </p>
        </div>
        <div className="form-actions">
          <button
            type="submit"
            className="btn-primary"
            disabled={save.isPending}
          >
            {t.save}
          </button>
        </div>
      </form>
    </Panel>
  );
}

/** Saved on change — there is nothing else in the form to wait for. */
function DayViewForm({ dayView }: { dayView: CalorieDayView }) {
  const t = useDict(caloriesDict);
  const save = useUpdateDayView();
  const labels: Record<CalorieDayView, string> = {
    cards: t.dayViewCards,
    chart: t.dayViewChart,
  };

  return (
    <Panel title={t.dayView}>
      <SelectField
        id="calories-day-view"
        label={t.dayViewSummary}
        // The pick, until the saved value comes back with it.
        value={save.isPending ? save.variables : dayView}
        disabled={save.isPending}
        onChange={(e) => save.mutate(e.target.value as CalorieDayView)}
      >
        {CALORIE_DAY_VIEWS.map((view) => (
          <option key={view} value={view}>
            {labels[view]}
          </option>
        ))}
      </SelectField>
    </Panel>
  );
}

function MealRow({
  meal,
  index,
  ids,
}: {
  meal: CalorieMealDto;
  index: number;
  /** Every meal id in the current order — what a move sends. */
  ids: string[];
}) {
  const formId = useId();
  const t = useDict(caloriesDict);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(meal.name);
  const rename = useRenameMeal();
  const reorder = useReorderMeals();
  const remove = useDeleteMeal();

  function move(by: -1 | 1) {
    const next = [...ids];
    [next[index], next[index + by]] = [next[index + by], next[index]];
    reorder.mutate(next);
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    rename.mutate(
      { id: meal.id, name },
      { onSuccess: () => setEditing(false) },
    );
  }

  if (editing) {
    return (
      <li className="entry-row-form">
        <form onSubmit={submit} className="meal-rename-form">
          <Field
            id={`${formId}-name`}
            label={t.mealName}
            type="text"
            autoComplete="off"
            required
            maxLength={NAME_MAX}
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <div className="form-actions">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                setName(meal.name);
                setEditing(false);
              }}
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={rename.isPending}
            >
              {t.save}
            </button>
          </div>
        </form>
      </li>
    );
  }

  return (
    <li className="entry-row">
      <div className="entry-main">
        <p className="entry-name">{meal.name}</p>
      </div>
      <div className="entry-actions">
        <button
          type="button"
          className="btn-icon-ghost"
          aria-label={t.moveUp(meal.name)}
          disabled={index === 0 || reorder.isPending}
          onClick={() => move(-1)}
        >
          <IconChevronUp />
        </button>
        <button
          type="button"
          className="btn-icon-ghost"
          aria-label={t.moveDown(meal.name)}
          disabled={index === ids.length - 1 || reorder.isPending}
          onClick={() => move(1)}
        >
          <IconChevronDown />
        </button>
        <button
          type="button"
          className="btn-icon-ghost"
          aria-label={t.rename(meal.name)}
          onClick={() => setEditing(true)}
        >
          <IconPencilSquare />
        </button>
        <DeleteButton
          label={t.deleteItem(meal.name)}
          confirm={t.confirmDeleteMeal(meal.name)}
          onConfirm={() => remove.mutate(meal.id)}
        />
      </div>
    </li>
  );
}

/** The sections a day is split into. None at all is a valid setup. */
function MealsEditor() {
  const formId = useId();
  const t = useDict(caloriesDict);
  const { data: meals } = useMeals();
  const create = useCreateMeal();
  const [name, setName] = useState('');
  const ids = meals?.map((m) => m.id) ?? [];

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    create.mutate({ name }, { onSuccess: () => setName('') });
  }

  return (
    <Panel title={t.meals}>
      {meals && meals.length > 0 ? (
        <ul className="entry-list">
          {meals.map((meal, index) => (
            <MealRow key={meal.id} meal={meal} index={index} ids={ids} />
          ))}
        </ul>
      ) : (
        <p className="meal-empty">{t.noMeals}</p>
      )}
      <form onSubmit={submit} className="meal-add-form">
        <Field
          id={`${formId}-new`}
          label={t.newMeal}
          type="text"
          autoComplete="off"
          required
          maxLength={NAME_MAX}
          placeholder={t.newMealPlaceholder}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button
          type="submit"
          className="btn-primary"
          disabled={create.isPending}
        >
          {t.addMeal}
        </button>
      </form>
    </Panel>
  );
}
